import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LEGAL_VERSION } from '../../shared/legalVersion';
import { acceptTerms } from '../services/api';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';

// Blocks access to the app until the user has accepted the current version
// of the CGU and the Privacy Policy. Covers two scenarios:
//   1. User signed up via Google OAuth and never saw the signup checkbox.
//   2. The legal documents were updated (LEGAL_VERSION incremented) and the
//      user's accepted version is now outdated.
//
// This component should be rendered inside AuthProvider, after the user
// profile has been loaded, so that user.termsAcceptedAt is available.

export default function TermsGate() {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  if (!user) return null;

  const needsAcceptance =
    !user.termsAcceptedAt || user.termsVersion !== LEGAL_VERSION;

  if (!needsAcceptance) return null;

  const isUpdate = !!user.termsAcceptedAt && user.termsVersion !== LEGAL_VERSION;

  const handleAccept = async () => {
    if (!checked) {
      setError("Vous devez cocher la case pour continuer.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await acceptTerms(LEGAL_VERSION);
      await refreshProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {isUpdate
                  ? 'Mise à jour des conditions'
                  : 'Conditions d\'utilisation'}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {isUpdate
                  ? 'Nos Conditions Générales d\'Utilisation et notre Politique de confidentialité ont été mises à jour. Veuillez en prendre connaissance et les accepter pour continuer à utiliser Evinly.'
                  : 'Avant de continuer, veuillez prendre connaissance de nos Conditions Générales d\'Utilisation et de notre Politique de confidentialité.'}
              </p>
            </div>
          </div>

          <label className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300 leading-relaxed cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              disabled={loading}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-violet-600 focus:ring-violet-500 cursor-pointer flex-shrink-0"
            />
            <span>
              J'ai lu et j'accepte les{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="underline text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 transition-colors"
              >
                Conditions Générales d'Utilisation
              </button>
              {' '}et la{' '}
              <button
                type="button"
                onClick={() => setShowPrivacy(true)}
                className="underline text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 transition-colors"
              >
                Politique de confidentialité
              </button>
              {' '}d'Evinly.
            </span>
          </label>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}

          <button
            onClick={handleAccept}
            disabled={loading || !checked}
            className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Enregistrement...' : 'Accepter et continuer'}
          </button>
        </div>
      </div>

      <PrivacyPolicy visible={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <TermsOfService visible={showTerms} onClose={() => setShowTerms(false)} />
    </>
  );
}
